import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import VoiceButton from "@/components/VoiceButton";

const INTENTS = [
  { key: "need", label: "I need something" },
  { key: "have", label: "I have something" },
  { key: "offer", label: "I offer a service" },
  { key: "know", label: "I know someone great" },
];

export default function CreateNeed() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    intent: params.get("intent") || "need",
    title: params.get("q") || "",
    description: "",
    category: "",
    tags: [],
    location: "",
    contact_method: "",
  });
  const [classifying, setClassifying] = useState(false);
  const [aiClassified, setAiClassified] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { if (!authLoading && !user) nav("/login"); }, [authLoading, user, nav]);
  useEffect(() => { api.get("/needs").then((r) => setCats(r.data.categories || [])); }, []);
  useEffect(() => {
    if (!editing) return;
    api.get(`/needs/${id}`).then(({ data }) => {
      setForm({
        intent: data.intent || "need",
        title: data.title || "",
        description: data.description || "",
        category: data.category || "",
        tags: data.tags || [],
        location: data.location || "",
        contact_method: data.contact_method || "",
      });
    });
  }, [id, editing]);

  const classify = async () => {
    const text = `${form.title}. ${form.description}`.trim();
    if (text.length < 5) return;
    setClassifying(true);
    try {
      const { data } = await api.post("/needs/classify", { text });
      setForm((f) => ({
        ...f,
        intent: f.intent || data.intent,
        category: data.category || f.category,
        tags: Array.from(new Set([...(f.tags || []), ...(data.tags || [])])).slice(0, 8),
      }));
      setAiClassified(true);
      toast.success("Categorized for you");
    } catch { toast.error("AI unavailable — please pick a category manually"); }
    setClassifying(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return toast.error("Add a short title and details");
    setSaving(true);
    try {
      const payload = { ...form, category: form.category || "Other" };
      const { data } = editing
        ? await api.patch(`/needs/${id}`, payload)
        : await api.post("/needs", payload);
      toast.success(editing ? "Updated" : "Posted — sharing next?");
      nav(`/needs/${data.id}`);
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">{editing ? "Edit" : "New post"}</div>
      <h1 className="font-display text-4xl text-[#1E3A2F] mt-1">{editing ? "Edit your post" : "Post to the community"}</h1>
      <p className="text-sm text-[#5C5C5C] mt-2">Say what you need or what you have. We'll auto-tag and route it.</p>

      <form onSubmit={submit} className="mt-8 space-y-6 bg-white border border-[#E2DED0] rounded-2xl p-6 sm:p-8">
        <div>
          <Label>Post type</Label>
          <Select value={form.intent} onValueChange={(v) => set("intent", v)}>
            <SelectTrigger data-testid="need-intent"><SelectValue /></SelectTrigger>
            <SelectContent>{INTENTS.map((i) => <SelectItem key={i.key} value={i.key}>{i.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>One-line summary</Label>
          <Input data-testid="need-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="I need a Kenyan caterer for a graduation party in Tacoma" />
        </div>
        <div>
          <Label>Details</Label>
          <Textarea data-testid="need-description" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Add dates, budget, preferences, or anything that helps the community respond." />
          <div className="mt-2 flex flex-wrap gap-3 items-center">
            <VoiceButton
              onTranscript={(text) => {
                setForm((f) => ({
                  ...f,
                  title: f.title || text.split(/[.!?]/)[0].slice(0, 120),
                  description: (f.description ? f.description + " " : "") + text,
                }));
              }}
            />
            <button type="button" data-testid="need-classify" onClick={classify} disabled={classifying || form.title.length < 3} className="inline-flex items-center gap-2 text-sm text-[#C05A3D] font-medium hover:underline disabled:opacity-50">
              {classifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiClassified ? "Re-run smart tag" : "Auto-tag with AI"}
            </button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={form.category || ""} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="need-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Location</Label>
            <Input data-testid="need-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Tacoma, WA" />
          </div>
        </div>
        {form.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {form.tags.map((t, i) => <span key={i} className="abm-chip">#{t}</span>)}
          </div>
        )}
        <div>
          <Label>How should people reach you?</Label>
          <Textarea data-testid="need-contact" rows={2} value={form.contact_method} onChange={(e) => set("contact_method", e.target.value)} placeholder="WhatsApp / email / DM here" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" className="rounded-full" onClick={() => nav(-1)}>Cancel</Button>
          <Button data-testid="need-submit" type="submit" disabled={saving} className="abm-btn-primary rounded-full px-6">
            {saving ? "Posting…" : editing ? "Save changes" : "Post to the community"}
          </Button>
        </div>
      </form>
    </div>
  );
}
